import { Doc } from '@dadohr/shared/domainTypes/document';
import { ITargetAudience } from '@dadohr/shared/domainTypes/targetAudience';
import styled from '@emotion/styled';
import { Button, ButtonBase, Popover, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Copy, Users } from 'lucide-react';
import { useRef, useState } from 'react';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useRoutes } from '../../../../../../routes';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { useAllTargetAudiences } from '../../../../../../services/targetAudience';
import { useUserRuleConfigWithAutocompletion } from '../../../../../../services/user';
import { RuleRenderer } from '../../../RuleBuilder';
import * as UX from '../../newux/kit';

// Built directly on MUI's Popover (same anchored-trigger mechanics
// apps/src/components/SingleSelector uses), rather than SingleSelector
// itself — SingleSelector's API only expresses a flat list of selectable
// `options`, with no slot for the header/description line or the loading
// and empty states this popover needs.

const POPOVER_WIDTH = 280;

const PopoverContent = styled('div')`
  width: ${POPOVER_WIDTH}px;
  border: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
  overflow: hidden;
`;

// Header: bold + dark so it reads as a clear label above the lighter
// description below — using the app's real weight tokens (fontWeightBold,
// which tops out at 400 in this theme, is as heavy as this app's type
// scale gets; there's no 700 token).
const PopoverHeader = styled('div')`
  padding: 10px 14px 4px;
  font-size: ${(p) => p.theme.custom.fontSize.s}px;
  font-weight: ${(p) => p.theme.typography.fontWeightBold};
  color: ${(p) => p.theme.custom.colors.gray.w900};
  text-transform: uppercase;
`;

// Description: smaller + lighter than the header, so it stays secondary
// and fits on two lines instead of wrapping to three.
const PopoverDescription = styled('div')`
  padding: 0 14px 10px;
  font-size: ${(p) => p.theme.custom.fontSize.xs}px;
  font-weight: ${(p) => p.theme.typography.fontWeightLight};
  color: ${(p) => p.theme.custom.colors.gray.w500};
  border-bottom: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
`;

const SegmentList = styled('div')`
  max-height: 220px;
  overflow-y: auto;
`;

const SegmentRow = styled(ButtonBase)`
  && {
    display: block;
    width: 100%;
    text-align: left;
    padding: 10px 14px;
    border-bottom: 1px solid ${(p) => p.theme.custom.colors.gray.w200};

    &:hover {
      background: ${(p) => p.theme.custom.colors.gray.w100};
    }
  }
`;

const SkeletonRows = styled('div')`
  padding: 6px 14px 12px;
`;

const SkeletonRow = styled('div')`
  margin-bottom: 10px;
`;

const EmptyState = styled('div')`
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

export const CopySegmentMenu = ({
  onCopy,
  newUx = false
}: {
  onCopy: (segment: Doc<ITargetAudience>) => void;
  newUx?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { ROUTES } = useRoutes();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const [segments, loading] = useAllTargetAudiences(org.id);
  const config = useUserRuleConfigWithAutocompletion(
    org.id,
    org.customUserFields,
    org.tz
  );

  const isEmpty = !loading && (segments?.length ?? 0) === 0;

  return (
    <>
      {newUx ? (
        <UX.RingIconButton
          ref={anchorRef}
          type="button"
          selected={open}
          onClick={() => setOpen((x) => !x)}
        >
          <Copy size={15} strokeWidth={1.5} />
          {t('Copy saved segment')}
        </UX.RingIconButton>
      ) : (
        <Button
          ref={anchorRef}
          size="small"
          color="primary"
          startIcon={<Copy size={16} />}
          onClick={() => setOpen((x) => !x)}
        >
          {t('Copy Saved Audience Segment')}
        </Button>
      )}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ elevation: 10 }}
      >
        <PopoverContent>
          <PopoverHeader>{t('Saved segments')}</PopoverHeader>
          <PopoverDescription>
            {t(
              "Copies the segment's filters as a new audience here. Edits here do not update the saved segment."
            )}
          </PopoverDescription>

          {loading && (
            <SkeletonRows>
              {[0, 1, 2].map((i) => (
                <SkeletonRow key={i}>
                  <Skeleton width="55%" height={12} />
                  <Skeleton width="85%" height={10} />
                </SkeletonRow>
              ))}
            </SkeletonRows>
          )}

          {isEmpty && (
            <EmptyState>
              <Users size={24} color="currentColor" />
              <Typography variant="body2" style={{ fontWeight: 400 }}>
                {t('No saved segments yet')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('Build one in Target Audiences to reuse it here.')}
              </Typography>
              <Button
                size="small"
                color="primary"
                onClick={() => {
                  window.open(ROUTES.users.targetAudiences.list.url(), '_blank');
                  setOpen(false);
                }}
              >
                {t('Go to Target Audiences ›')}
              </Button>
            </EmptyState>
          )}

          {!loading && !isEmpty && (
            <SegmentList>
              {segments?.map((seg) => (
                <SegmentRow
                  key={seg.id}
                  onClick={() => {
                    setOpen(false);
                    onCopy(seg);
                  }}
                >
                  <Typography variant="body2" style={{ fontWeight: 400 }}>
                    {seg.data.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    <RuleRenderer
                      value={seg.data.rule}
                      config={config}
                      variant="oneline"
                    />
                  </Typography>
                </SegmentRow>
              ))}
            </SegmentList>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};
